/**
 * Normalizes capability API payloads so admin + portal pages always receive
 * camelCase fields and a non-null subCapabilities array.
 */

import {
  QUESTION_STATE,
  normalizeQuestionList,
} from './subCapabilityQuestionUtils';

function readAssessmentFields(source) {
  return {
    currentStateCommentPlaceholder:
      source?.currentStateCommentPlaceholder ?? source?.CurrentStateCommentPlaceholder ?? '',
    futureStateCommentPlaceholder:
      source?.futureStateCommentPlaceholder ?? source?.FutureStateCommentPlaceholder ?? '',
    maturityOptionsJson: source?.maturityOptionsJson ?? source?.MaturityOptionsJson ?? null,
  };
}

export function normalizeSubCapabilityFromApi(subCap, capabilityId = 0) {
  return {
    subId: subCap?.subId ?? subCap?.SubId ?? subCap?.id ?? subCap?.Id ?? 0,
    capabilityId: subCap?.capabilityId ?? subCap?.CapabilityId ?? capabilityId,
    subCapabilityName: subCap?.subCapabilityName ?? subCap?.SubCapabilityName ?? subCap?.name ?? subCap?.Name ?? '',
    subCapabilityDescription:
      subCap?.subCapabilityDescription ?? subCap?.SubCapabilityDescription ?? subCap?.description ?? subCap?.Description ?? '',
    isActive: subCap?.isActive ?? subCap?.IsActive ?? true,
    currentStateQuestions: normalizeQuestionList(
      subCap?.currentStateQuestions ?? subCap?.CurrentStateQuestions,
      QUESTION_STATE.current
    ),
    futureStateQuestions: normalizeQuestionList(
      subCap?.futureStateQuestions ?? subCap?.FutureStateQuestions,
      QUESTION_STATE.future
    ),
    ...readAssessmentFields(subCap),
  };
}

export function normalizeCapabilityFromApi(capability) {
  const id = capability?.id ?? capability?.Id ?? 0;
  const rawSubs =
    capability?.subCapabilities ??
    capability?.SubCapabilities ??
    capability?.subCapabilityList ??
    [];
  const subCapabilities = (Array.isArray(rawSubs) ? rawSubs : []).map((sub) =>
    normalizeSubCapabilityFromApi(sub, id)
  );
  const hasNamedSubs = subCapabilities.some((sub) =>
    String(sub.subCapabilityName || '').trim()
  );

  return {
    ...capability,
    id,
    capabilityId: capability?.capabilityId ?? capability?.CapabilityId ?? id,
    capabilityName: capability?.capabilityName ?? capability?.CapabilityName ?? capability?.name ?? '',
    capabilityDescription:
      capability?.capabilityDescription ?? capability?.CapabilityDescription ?? capability?.description ?? '',
    capabilityIconUrl: capability?.capabilityIconUrl ?? capability?.CapabilityIconUrl ?? capability?.iconURL ?? '',
    businessTypeId: capability?.businessTypeId ?? capability?.BusinessTypeId,
    frameworkId: capability?.frameworkId ?? capability?.FrameworkId ?? null,
    sourceFrameworkName:
      capability?.sourceFrameworkName ??
      capability?.SourceFrameworkName ??
      capability?.frameworkTitle ??
      capability?.FrameworkTitle ??
      '',
    isActive: capability?.isActive ?? capability?.IsActive ?? true,
    currentStateQuestions: hasNamedSubs
      ? []
      : normalizeQuestionList(
          capability?.currentStateQuestions ?? capability?.CurrentStateQuestions,
          QUESTION_STATE.current
        ),
    futureStateQuestions: hasNamedSubs
      ? []
      : normalizeQuestionList(
          capability?.futureStateQuestions ?? capability?.FutureStateQuestions,
          QUESTION_STATE.future
        ),
    ...(hasNamedSubs
      ? {
          currentStateCommentPlaceholder: '',
          futureStateCommentPlaceholder: '',
          maturityOptionsJson: null,
        }
      : readAssessmentFields(capability)),
    subCapabilities,
  };
}

export function normalizeCapabilitiesList(capabilities) {
  if (!Array.isArray(capabilities)) {
    return [];
  }
  return capabilities.map(normalizeCapabilityFromApi);
}
